import { useEffect, useRef, useState } from 'react'
import type { SupportedLocale } from '../shared/i18n/translations'

const languages = [
  { value: 'es-ES', flag: 'es', label: 'Español' },
  { value: 'en-GB', flag: 'gb', label: 'English' },
  { value: 'fr-FR', flag: 'fr', label: 'Français' },
  { value: 'de-DE', flag: 'de', label: 'Deutsch' },
  { value: 'it-IT', flag: 'it', label: 'Italiano' },
  { value: 'pt-PT', flag: 'pt', label: 'Português' },
  { value: 'ca-ES', flag: 'ca', label: 'Català' },
] as const

function Flag({ country }: { country: (typeof languages)[number]['flag'] }) {
  if (country === 'gb') {
    return (
      <svg className="language-flag" viewBox="0 0 24 16" aria-hidden="true">
        <rect width="24" height="16" fill="#21468b" />
        <path d="M0 0 24 16M24 0 0 16" stroke="#fff" strokeWidth="4" />
        <path d="M0 0 24 16M24 0 0 16" stroke="#cf142b" strokeWidth="1.7" />
        <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5" />
        <path d="M12 0v16M0 8h24" stroke="#cf142b" strokeWidth="2.7" />
      </svg>
    )
  }

  const stripes = {
    es: ['#aa151b', '#f1bf00', '#aa151b'],
    fr: ['#002395', '#fff', '#ed2939'],
    de: ['#000', '#dd0000', '#ffce00'],
    it: ['#009246', '#fff', '#ce2b37'],
    pt: ['#046a38', '#da291c'],
    ca: ['#f7d417', '#c60b1e', '#f7d417', '#c60b1e', '#f7d417', '#c60b1e', '#f7d417', '#c60b1e', '#f7d417'],
  }[country]
  const vertical = country === 'fr' || country === 'it' || country === 'pt'

  return (
    <svg className="language-flag" viewBox="0 0 24 16" aria-hidden="true">
      {stripes.map((color, index) => (
        <rect
          key={`${color}-${index}`}
          x={vertical ? (24 / stripes.length) * index : 0}
          y={vertical ? 0 : (16 / stripes.length) * index}
          width={vertical ? 24 / stripes.length : 24}
          height={vertical ? 16 : 16 / stripes.length}
          fill={color}
        />
      ))}
    </svg>
  )
}

export function LanguageSelector({
  locale,
  label,
  onChange,
}: {
  locale: SupportedLocale
  label: string
  onChange: (locale: SupportedLocale) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = languages.find((language) => language.value === locale) ?? languages[0]

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  return (
    <div className="language-selector" ref={rootRef}>
      <button
        type="button"
        className="locale-select"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Flag country={selected.flag} />
        <span className="selector-current-label">{selected.label}</span>
        <span className="language-chevron" aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div className="language-menu" role="listbox" aria-label={label}>
          {languages.map((language) => (
            <button
              type="button"
              role="option"
              aria-selected={language.value === locale}
              className={language.value === locale ? 'selected' : undefined}
              key={language.value}
              onClick={() => {
                onChange(language.value)
                setOpen(false)
              }}
            >
              <Flag country={language.flag} />
              <span>{language.label}</span>
              {language.value === locale && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
